import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

// Top-level error boundary. Without this, a single component throw blanks
// the app. The fallback gives the user a path to recover (refresh) and
// surfaces enough detail for triage when in development.

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    // Sentry.captureException is a no-op when init() was skipped (no DSN).
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? '' } },
    });
  }

  reset = (): void => this.setState({ error: null, info: null });

  render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const isDev = import.meta.env.DEV;
    return (
      <div className="container stack" style={{ maxWidth: 560, marginTop: '4rem' }}>
        <div className="card stack">
          <div className="dim" style={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
            Boulder Bash
          </div>
          <h1 style={{ margin: 0 }}>Something broke</h1>
          <p style={{ color: 'var(--fg-dim)', margin: 0 }}>
            The page hit an error and couldn't finish rendering. Refresh to try again. If this keeps happening,
            let your gym admin know — they can pass the details on.
          </p>
          <div className="row" style={{ gap: '0.5rem' }}>
            <button onClick={() => window.location.reload()}>Refresh</button>
            <button className="ghost" onClick={this.reset}>Try again</button>
            <a className="ghost" href="/" style={{ padding: '0.6rem 0.9rem', borderRadius: 'var(--radius)' }}>Home</a>
          </div>
          {isDev && (
            <details style={{ marginTop: '0.75rem' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--fg-dim)' }}>Error details (dev only)</summary>
              <pre style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                overflow: 'auto',
                fontSize: '0.78rem',
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
              }}>
{error.message}{'\n'}{error.stack ?? ''}{info?.componentStack ?? ''}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
